import SRCards from '../../components/SRCards';
import React, { useState, useEffect } from 'react';
import {
    MenuItem,
    Select,
    Snackbar,
    Chip,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Box,
    FormControl,
    InputLabel,
} from '@material-ui/core';
import UserApi from '../../services/user.service';
import {
    CHECK_NON_EMPTY_ARRAY,
    CHECK_VALUE,
} from '../../common/data-validator';
import _ from 'lodash';
import { User, Role, Permission } from './interfaces/user.interface';
import { Alert } from '@material-ui/lab';

type SnackbarSeverity = 'success' | 'error' | 'info' | 'warning';
type SnackbarState = {
    open: boolean;
    message: string;
    severity: SnackbarSeverity;
};

const UserList = () => {
    const [users, setUsers] = useState<Array<User>>([]);
    const [search, setSearch] = useState('');
    const [selectedUser, setSelectedUser] = useState<User>({} as User);
    const [showUserDialog, setShowUserDialog] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [roles, setRoles] = useState<Role[]>([] as Role[]);
    const [permissions, setPermissions] = useState<Array<Permission>>(
        [] as Permission[]
    );
    const [selectedPermissions, setSelectedPermissions] = useState<string[]>(
        []
    );
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [snackbar, setSnackbar] = useState<SnackbarState>({
        open: false,
        message: '',
        severity: 'success',
    });

    useEffect(() => {
        const page = 1;
        const limit = 100;
        UserApi.getRoles('', limit, page).then(setRoles).catch(()=> showSnackbar('Failed to get roles', 'error'));
        UserApi.getPermissions('', limit, page)
            .then(setPermissions)
            .catch(()=> showSnackbar('Failed to get permissions', 'error'));
    }, []);

    const searchUser = () => {
        setIsLoading(true);
        UserApi.getUsers(search)
            .then(setUsers)
            .catch(()=> showSnackbar('Failed to search users', 'error'))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        searchUser();
    }, [search]);

    const showSnackbar = (message: string, severity: SnackbarSeverity) => {
        setSnackbar({ open: true, message, severity });
    };

    const resetStates = () => {
        setSelectedUser({} as User);
        setSelectedPermissions([]);
    };

    const getRoleName = (roleId: string = '') => {
        return roles.find((role) => role._id === roleId)?.name || '';
    };
    const handleEditClick = (user: User) => {
        const userInfo = _.clone(user);
        setSelectedUser(userInfo);
        const permissionIds = CHECK_NON_EMPTY_ARRAY(userInfo.permissions)
            ? userInfo.permissions
            : [];
        const permissionNames = getPermissionNames(permissionIds!);
        const roleName = getRoleName(userInfo.roleId!);
        userInfo.role = roleName;
        setSelectedPermissions(permissionNames);
        setIsEditMode(true);
        setShowUserDialog(true);
    };

    const handleAddClick = () => {
        setSelectedUser({} as User);
        setIsEditMode(false);
        setShowUserDialog(true);
    };

    const handleClose = () => {
        setShowUserDialog(false);
        setSelectedUser({} as User);
        setIsEditMode(false);
        resetStates();
    };

    const verifyUserPayload = (userData: User) => {
        const newErrors: { [key: string]: string } = {};

        if (!userData.firstName || userData.firstName.trim() === '') {
            newErrors.firstName = 'First Name is required';
        }
        if (!userData.userName || userData.userName.trim() === '') {
            newErrors.userName = 'Email is required';
        }
        if (!userData.role || userData.role.trim() === '') {
            newErrors.role = 'Role is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const getPermissionNames = (permissionsIds: Array<string>) => {
        const permissionNames = permissions
            .filter((permission) => permissionsIds.includes(permission._id!))
            .map((permission) => permission.name!)
            .filter(Boolean);
        return permissionNames;
    };

    const addRoleAndPermissionToUser = (user: User) => {
        const userRole = user.role;
        const roleId =
            roles.find((role) => role.name === userRole)?._id || null;
        user.roleId = roleId;
        const permissionIds = permissions
            .filter((permission) =>
                selectedPermissions.includes(permission.name)
            )
            .map((permission) => permission._id!)
            .filter(Boolean);
        user.permissions = Array.from(new Set(permissionIds));
    };

    const handleSave = () => {
        const user = _.clone(selectedUser);
        if (!verifyUserPayload(user)) {
            return; // stop submission if there are errors
        }
        addRoleAndPermissionToUser(user);
        if (!isEditMode) {
            //creating a new User
            UserApi.createUser(user)
                .then((res) => {
                    showSnackbar('Successfully created the user', 'success');
                    searchUser();
                })
                .catch(() => showSnackbar('Failed to create user.', 'error'));
        } else {
            //updating User
            const updateUserPayload: any = user;
            if (updateUserPayload?.userName) {
                delete updateUserPayload?.userName;
            }
            const userId = user._id!;
            if (!CHECK_VALUE(userId)) {
                console.error('User id cannot be empty');
                return;
            }
            UserApi.updateUser(userId, updateUserPayload)
                .then((res) => {
                    showSnackbar('Successfully updated the user', 'success');
                    searchUser();
                })
                .catch(() => showSnackbar('Failed to update user.', 'error'));
        }
        handleClose();
    };

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        if (isEditMode && name === 'userName') return; // cannot change user email on edit
        setSelectedUser((prev) => ({ ...prev, [name]: value }));
    };

    const handlePermissionChange = (e: any) => {
        const value = e.target.value || [];
        setSelectedPermissions(value as string[]);
    };

    return (
        <>
            <SRCards />
            <Container style={{ marginTop: 40 }}>
                <Box
                    display='flex'
                    justifyContent='space-between'
                    alignItems='center'
                    mb={2}
                >
                    <Typography variant='h5'>User Management</Typography>
                    <Button
                        variant='contained'
                        color='primary'
                        onClick={handleAddClick}
                    >
                        + Add User
                    </Button>
                </Box>

                {/* Search Bar */}
                <Box mb={2}>
                    <TextField
                        label='Search users...'
                        variant='outlined'
                        fullWidth
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Box>

                {/* Users Table */}
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <strong>S.No</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Name</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Email</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Role</strong>
                                </TableCell>
                                <TableCell align='center'>
                                    <strong>Actions</strong>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        {isLoading && (
                            <TableBody>
                                <TableRow>
                                    <TableCell colSpan={4} align='center'>
                                        Tracking down users… almost there!
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )}
                        {!isLoading && (
                            <TableBody>
                                {users.length > 0 ? (
                                    users.map((user, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>
                                                {user.firstName ?? 'NA'}{' '}
                                                {user.lastName ?? ''}
                                            </TableCell>
                                            <TableCell>
                                                {user.userName}
                                            </TableCell>
                                            <TableCell>{user.role}</TableCell>
                                            <TableCell align='center'>
                                                <Button
                                                    variant='outlined'
                                                    color='primary'
                                                    onClick={() =>
                                                        handleEditClick(user)
                                                    }
                                                >
                                                    Edit
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4} align='center'>
                                            No users found. Did they hide from
                                            us?
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        )}
                    </Table>
                </TableContainer>

                {/* Add/Edit Dialog */}
                <Dialog open={showUserDialog} onClose={handleClose}>
                    <DialogTitle>
                        {!isEditMode ? 'Add New User' : 'Edit User'}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            margin='dense'
                            label='First Name'
                            name='firstName'
                            value={selectedUser?.firstName || ''}
                            onChange={handleChange}
                            error={!!errors.firstName}
                            fullWidth
                        />
                        <TextField
                            margin='dense'
                            label='Last Name'
                            name='lastName'
                            value={selectedUser?.lastName || ''}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            margin='dense'
                            label='Email'
                            name='userName'
                            disabled={isEditMode}
                            value={selectedUser?.userName || ''}
                            onChange={handleChange}
                            error={!!errors.userName}
                            fullWidth
                        />
                        <TextField
                            select
                            margin='dense'
                            label='Role'
                            name='role'
                            value={selectedUser.role || ''}
                            onChange={handleChange}
                            error={!!errors.role}
                            fullWidth
                        >
                            {roles.map((role, idx) => (
                                <option key={idx} value={role.name}>
                                    {role.name}
                                </option>
                            ))}
                        </TextField>
                        {/* Permissions Multi-select */}
                        <FormControl fullWidth margin='dense'>
                            <InputLabel id='permissions-label'>
                                Additional Permissions
                            </InputLabel>
                            <Select
                                labelId='permissions-label'
                                multiple
                                value={selectedPermissions || []}
                                onChange={handlePermissionChange}
                                renderValue={(selectedValues) => {
                                    const values: string[] =
                                        selectedValues as any;
                                    return (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            {values.map((permission) => (
                                                <Chip
                                                    key={permission}
                                                    label={permission}
                                                />
                                            ))}
                                        </Box>
                                    );
                                }}
                            >
                                {permissions.map((perm) => (
                                    <MenuItem key={perm._id} value={perm.name}>
                                        {perm.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} color='secondary'>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            color='primary'
                            variant='contained'
                        >
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={() =>
                            setSnackbar({ ...snackbar, open: false })
                        }
                        severity={snackbar.severity}
                        variant='filled'
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </>
    );
};

export default UserList;
